import { Check } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import FormHeader from "../../../components/form/FormHeader";
import { Button } from "../../../components";
import { goBackOrGoHome } from "../../../utils/navigation";
import NewsForm, { type NewsFormSubmitValues, type NewsFormValues } from "../components/form/NewsForm";
import { useGetNewsDetail } from "../hooks/useGetNewsDetail";
import { useUpdateNews } from "../hooks/useUpdateNews";

function UpdateNewsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { newsDetail, loading: detailLoading, error: detailError, refetch } = useGetNewsDetail(id);
  const { updateNews, loading: updating, error: updateError, success } = useUpdateNews();

  const defaultValues = useMemo<Partial<NewsFormValues> | undefined>(() => {
    if (!newsDetail) {
      return undefined;
    }

    return {
      title: newsDetail.title ?? "",
      content: newsDetail.content ?? "",
      location: newsDetail.location ?? "",
      thumbnail_url: newsDetail.thumbnail_url ?? "",
    };
  }, [newsDetail]);

  useEffect(() => {
    if (!id) {
      navigate("/news", { replace: true });
    }
  }, [id, navigate]);

  useEffect(() => {
    if (success && id) {
      navigate(`/news/view/${id}`, { replace: true });
    }
  }, [id, navigate, success]);

  const handleSubmit = async (values: NewsFormSubmitValues) => {
    if (!id) {
      return;
    }

    await updateNews(id, {
      title: values.title.trim(),
      content: values.content.trim(),
      thumbnail_url: values.thumbnail_url,
      location: values.location?.trim() || undefined,
      thumbnail_file: values.thumbnail_file,
    });
  };

  if (!id) {
    return null;
  }

  const isLoading = detailLoading && !newsDetail;

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-sm text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  if (detailError && !newsDetail) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex max-w-md flex-col items-center gap-4 px-6 text-center">
          <p className="text-sm text-red-600">{detailError}</p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => void refetch()}>
              {t("ui.retry")}
            </Button>
            <Button onClick={() => navigate("/news")}>{t("common.back")}</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <FormHeader
        onBack={() => goBackOrGoHome(navigate)}
        title={t("news.update_news")}
        subtitle=""
        backLabel={t("common.back")}
        actions={
          <Button type="submit" form="news-update-form" disabled={updating} className="shrink-0">
            <Check className="h-4 w-4" />
            {updating ? t("common.saving") : t("common.save_changes")}
          </Button>
        }
      />

      <NewsForm
        formId="news-update-form"
        loading={updating}
        error={updateError}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
      />
    </div>
  );
}

export default UpdateNewsPage;
